import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import TeamFlag from "./TeamFlag";
import { useActiveGroup } from "../contexts/ActiveGroupContext";

export default function MatchBetsModal({ matchId, groupId, onClose, onUserClick }) {
  const { t } = useI18n();
  const { activeGroup } = useActiveGroup();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/matches/${matchId}/bets`, {
          params: groupId ? { group_id: groupId } : {}
        });
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || t("could_not_load"));
      }
    })();
  }, [matchId, groupId]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="match-bets-modal"
      >
        <motion.div
          initial={{ y: 60 }}
          animate={{ y: 0 }}
          exit={{ y: 60 }}
          transition={{ type: "spring", damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[86vh] flex flex-col overflow-hidden"
        >
          <div className="p-5 border-b border-neutral-100 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-400">{t("everyone_s_tips")}</div>
              {data?.match && (
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mt-2">
                  <div className="flex items-center gap-2 min-w-0 justify-start">
                    <TeamFlag team={data.match.home_team} size={32} />
                    <span className="font-bold text-sm truncate">{data.match.home_team?.name}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="font-mono-tab text-xl font-bold text-[#02371b]">
                      {data.match.home_score ?? "–"}:{data.match.away_score ?? "–"}
                    </div>
                    {data.match.finished && (
                      <>
                        {data.match.match_duration === "PENALTY_SHOOTOUT" && (
                          <div className="text-[9px] font-bold text-neutral-500 mt-0.5 uppercase tracking-wider text-center">
                            {t("penalties")} {data.match.home_score_penalties} - {data.match.away_score_penalties}
                          </div>
                        )}
                        {data.match.match_duration === "EXTRA_TIME" && (
                          <div className="text-[9px] font-bold text-neutral-500 mt-0.5 uppercase tracking-wider text-center">
                            {t("after_extra_time")}
                          </div>
                        )}
                        {activeGroup?.scoring_system === "four_point" &&
                         (data.match.match_duration === "EXTRA_TIME" || data.match.match_duration === "PENALTY_SHOOTOUT") && (
                          <div className="text-[9px] text-neutral-400 mt-0.5 text-center">
                            {t("main_time")}: {data.match.home_score_regular ?? 0}:{data.match.away_score_regular ?? 0}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 min-w-0 justify-end">
                    <span className="font-bold text-sm truncate">{data.match.away_team?.name}</span>
                    <TeamFlag team={data.match.away_team} size={32} />
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="close-match-bets"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center flex-shrink-0"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {error && <div className="text-sm text-destructive p-4">{error}</div>}
            {!data && !error && (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3 py-3.5 px-3 border-b border-neutral-100 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-neutral-100 border border-neutral-200 flex-shrink-0" />
                    <div className="flex-1 h-4 bg-neutral-100 rounded-md max-w-[120px]" />
                    <div className="h-4 bg-neutral-100 rounded-md w-8 ml-auto" />
                    <div className="h-4 bg-neutral-100 rounded-md w-6" />
                  </div>
                ))}
              </div>
            )}
            {data && data.bets.length === 0 && (
              <div className="text-center py-10 text-sm text-neutral-500">{t("no_bets_on_match")}</div>
            )}
            {data?.bets.map((b) => (
              <button
                key={b.user_id}
                type="button"
                data-testid={`match-bet-user-${b.user_id}`}
                onClick={() => onUserClick?.(b.user_id)}
                className="w-full flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-neutral-50 border-b border-neutral-100 last:border-0 text-left"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral-100 border border-neutral-200 flex items-center justify-center flex-shrink-0">
                  {b.picture ? (
                    <img src={b.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[11px] font-bold text-neutral-500">{(b.name || "?")[0]?.toUpperCase()}</span>
                  )}
                </div>
                <span className="flex-1 text-sm font-bold truncate text-[#02371b]">{b.name}</span>
                <span className="font-mono-tab text-sm font-bold text-[#02371b]">{b.home_score}:{b.away_score}</span>
                {b.points != null && (
                  <span className={`font-mono-tab text-xs font-bold w-10 text-right ${b.points >= 3 ? "text-[#084d25]" : b.points > 0 ? "text-[#41a42b]" : "text-neutral-400"}`}>
                    +{b.points}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
